import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import DataTable from 'src/components/DataTable';
import { resolveQuickSearch } from 'src/components/SearchFilterBar';
import { useGetBookedFromTransfer } from 'src/pages/ProjectDetailsPage/api/queries/useGetBookedFromTransfer';

import messages from '../../messages';
import { useStyles } from '../../JobDetails.styled';
import ContractTooltip from '../../../ContractTooltip';
import { forceString } from 'src/utils/datetime';

export default function JobTransferred({
  jobUuid,
  standbyId = 0,
  filters,
}: UCM.JobStandbyTabProps) {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const { data, isLoading } = useGetBookedFromTransfer({
    jobUuid,
    standbyId,
    enabled: Boolean(jobUuid) && Boolean(standbyId),
    filters: resolveQuickSearch(filters),
  });

  const columns: Array<{
    id: keyof UCM.JobApplicationType | string;
    label: string;
  }> = useMemo(
    () => [
      {
        id: 'applicant',
        label: formatMessage(messages.applicantLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item?.applicant) return '-';
          const { id, fullName } = item.applicant;
          return (
            <Typography
              variant="body1"
              fontWeight={(theme) => theme.typography.fontWeightMedium}
            >
              {`${id} - ${fullName}`}
            </Typography>
          );
        },
      },
      {
        id: 'appliedCount',
        label: formatMessage(messages.appliedToLabel),
        render: (appliedCount: number) => (
          <Typography variant="inherit">{appliedCount ?? '-'}</Typography>
        ),
        align: 'center',
      },
      // TODO - waiting for backend adjustements
      // {
      //   id: 'workday',
      //   label: formatMessage(messages.workdayLabel),
      //   // TODO - waiting for backend adjustements
      //   render: () => <Typography variant="inherit">DD/MM/YYYY</Typography>,
      //   align: 'center',
      // },
      {
        id: 'ratingScore',
        label: formatMessage(messages.ratingLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item?.applicant.ratingScore) return '-';
          return (
            <Typography variant="inherit">
              {item?.applicant.ratingScore}
            </Typography>
          );
        },
        align: 'center',
      },
      // TODO - waiting for backend adjustements
      // {
      //   id: 'preScreen',
      //   label: formatMessage(messages.preScreenLabel),
      //   render: (_: Object, item: UCM.JobApplicationType) => {
      //     const { prescreeningRating } = item.applicant;

      //     return (
      //       <Tooltip
      //         arrow
      //         title={
      //           <Stack spacing={1}>
      //             {Object.entries(prescreeningRating).map(([key, value]) => (
      //               <Typography
      //                 key={key}
      //                 variant="inherit"
      //                 textTransform="capitalize"
      //               >
      //                 {key}: {value}
      //               </Typography>
      //             ))}
      //           </Stack>
      //         }
      //       >
      //         <Typography variant="inherit">
      //           {prescreeningRating.average}
      //         </Typography>
      //       </Tooltip>
      //     );
      //   },
      //   align: 'center',
      // },
      {
        id: 'profile',
        label: formatMessage(messages.profileLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant?.status) return '-';
          let label = formatMessage(messages.completeLabel);
          if (item.applicant?.status?.code === 'required')
            label = formatMessage(messages.incompleteLabel);
          return <Typography variant="inherit">{label}</Typography>;
        },
        align: 'center',
      },
      {
        id: 'contractSummary',
        label: formatMessage(messages.contractSummaryLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item?.contractSummary.status) return '-';
          return <ContractTooltip contract={item?.contractSummary} />;
        },
        align: 'center',
      },
      {
        id: 'transferredBy',
        align: 'center',
        label: formatMessage(messages.transferredByLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          const { transferData } = item;

          if (!(transferData?.transferredAt && transferData?.transferredBy)) {
            return '-';
          }

          return (
            <>
              <Typography variant="inherit" textTransform="capitalize">
                {transferData.transferredBy}
              </Typography>

              <Typography variant="inherit">
                {forceString(transferData.transferredAt)}
              </Typography>
            </>
          );
        },
      },
    ],
    [],
  );

  return (
    <Grid size={12}>
      <DataTable
        rows={data}
        columns={columns}
        className={classes.tableContainer}
        loading={isLoading}
        enabledFooter={false}
      />
    </Grid>
  );
}
